;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

// lib files
import createStorage from "@/lib/helpers/createStorage";
import { canAccessStorage } from '@/lib/helpers/is-storage-available';

// types
import { ProfileData } from "@/lib/types/api/profile-data"
import { ProfileState } from '@/lib/types/identity';

const PROFILE_KEY = 'profile'
const CONFIRMED_SESSION_KEY = 'confirmed-session'
/** This page load's outcome. In memory, so it starts unknown on every load. */
export type Confirmation = 'unknown' | 'authenticated' | 'anonymous' | 'error'

export interface ConfirmedSession {
  sessionId: string
  confirmedAt: number
}

const profileStorage = createStorage<ProfileData | null>('localStorage')
const confirmedSessionStorage = createStorage<ConfirmedSession | null>('localStorage')

/** Guarded: this runs at module evaluation, where a throw takes down every importer. */
function readStored<T>(key: string): T | null {
  if (!canAccessStorage('localStorage')) return null

  const raw = window.localStorage.getItem(key)
  if (raw === null || raw === 'null' || raw === 'undefined') return null

  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

/**
 * The profile as last fetched. Survives reloads, so a returning visitor paints
 * immediately. Holding it is what `isSignedIn` answers; it is not proof of a live
 * session.
 */
export const userProfile = atomWithStorage<ProfileData | null>(
  PROFILE_KEY,
  readStored<ProfileData>(PROFILE_KEY),
  profileStorage,
)

export const profileConfirmationAtom = atom<Confirmation>('unknown')

/**
 * Assembles the lifecycle from what we hold and what this page load confirmed.
 *
 * `revalidating` is derived rather than set, so no producer can omit it.
 */
export function toProfileState(
  profile: ProfileData | null,
  confirmation: Confirmation,
): ProfileState {
  switch (confirmation) {
    case 'authenticated':
      // No consumer treats a confirmation without a profile as signed in.
      return profile ? { status: 'authenticated', profile } : { status: 'anonymous', profile: null }
    case 'anonymous':
      return { status: 'anonymous', profile: null }
    case 'error':
      return { status: 'error', profile }
    case 'unknown':
      return profile ? { status: 'revalidating', profile } : { status: 'unknown', profile: null }
  }
}

/** Read-only view of the profile atoms as a ProfileState. */
export const profileStateAtom = atom<ProfileState>((get) =>
  toProfileState(get(userProfile), get(profileConfirmationAtom))
)

export const confirmedSessionCacheAtom = atomWithStorage<ConfirmedSession | null>(
  CONFIRMED_SESSION_KEY,
  readStored<ConfirmedSession>(CONFIRMED_SESSION_KEY),
  confirmedSessionStorage,
)
